import { stripEmptyInlineCode } from '../inlineCode';
import { escapeHtml } from './htmlEscape';
import { isSafeHref } from './linkSafety';

/** True when href has a real dotted hostname (blocks mid-type stubs like https://a). */
const isLinkifiableHttpUrl = (href) => {
  if (!isSafeHref(href) || !/^https?:\/\//i.test(href)) return false;
  try {
    const { hostname } = new URL(href);
    // Require label.tld — single-label hosts (https://a, localhost) stay plain text
    return hostname.includes('.') && /(?:^|\.)[a-zA-Z]{2,}$/.test(hostname);
  } catch {
    return false;
  }
};

/**
 * Auto-link bare http(s) URLs once they look finished.
 * While typing, a URL at the end of the line must NOT be wrapped yet — otherwise
 * `https://a` becomes a link and the rest of the domain lands outside the <a>
 * (e.g. https://apps.auddict.com → linked "https://a" + plain "pps.auddict.com").
 * Paste passes linkifyAtEnd so a lone pasted URL still becomes a link.
 */
const linkifyBareUrls = (text, { linkifyAtEnd }) =>
  text.replace(/https?:\/\/[^\s<]+/gi, (raw, offset) => {
    const m = String(raw).match(/^(https?:\/\/[^\s<]+?)([.,;:!?]+)?$/i);
    const href = m ? m[1] : raw;
    const trail = m && m[2] ? m[2] : '';
    const after = text.slice(offset + raw.length);
    const completed =
      trail.length > 0 ||
      /^(?:\s|&nbsp;|<)/i.test(after) ||
      (linkifyAtEnd && after === '');
    if (!completed || !isLinkifiableHttpUrl(href)) return raw;
    return `<a href="${href}">${href}</a>${trail}`;
  });

/**
 * Repair links truncated mid-type: <a href="https://a">https://a</a>pps.auddict.com
 * and stale hrefs when text grew inside the anchor.
 */
const healTruncatedUrlLinks = (html) => {
  let out = String(html);
  // Continuation text after a URL-as-label anchor
  out = out.replace(
    /<a\b([^>]*)\bhref="(https?:\/\/[^"]+)"([^>]*)>\2<\/a>([A-Za-z0-9._~:/?#@[\]!$&'()*+,;=%-]+)/gi,
    (match, pre, href, post, rest) => {
      const combined = `${href}${rest}`;
      const m = combined.match(/^(https?:\/\/[^\s<]+?)([.,;:!?]+)?$/i);
      const nextHref = m ? m[1] : combined;
      const trail = m && m[2] ? m[2] : '';
      if (!isLinkifiableHttpUrl(nextHref)) return match;
      return `<a${pre}href="${nextHref}"${post}>${nextHref}</a>${trail}`;
    }
  );
  // Text grew inside the anchor but href stayed at the stub
  out = out.replace(
    /<a\b([^>]*)\bhref="(https?:\/\/[^"]+)"([^>]*)>(https?:\/\/[^<]+)<\/a>/gi,
    (match, pre, href, post, label) => {
      if (label === href) return match;
      const m = String(label).match(/^(https?:\/\/[^\s<]+?)([.,;:!?]+)?$/i);
      const nextHref = m ? m[1] : label;
      const trail = m && m[2] ? m[2] : '';
      if (!isLinkifiableHttpUrl(nextHref)) return match;
      // Only rewrite when the visible text is itself a URL (not a custom label)
      if (!label.startsWith(href) && nextHref !== label && nextHref + trail !== label) {
        return match;
      }
      return `<a${pre}href="${nextHref}"${post}>${nextHref}</a>${trail}`;
    }
  );
  return out;
};

/**
 * Apply `code` / **bold** / *italic* / [text](url) / bare-URL markers inside a
 * plain (already-escaped) string. Code spans are converted first so URLs
 * inside backticks stay literal.
 */
const convertMarkdownMarkersInText = (text, { linkifyAtEnd = false } = {}) => {
  if (!text || !/[*`\[\]]|https?:\/\//i.test(text)) return text;
  // 1) inline code — protect those segments from linkify/bold
  let t = text.replace(/`([^`]+)`/g, '<code>$1</code>');
  let inCode = 0;
  t = t
    .split(/(<[^>]+>)/g)
    .map((part) => {
      if (part && part.startsWith('<')) {
        if (/^<code\b/i.test(part)) inCode += 1;
        else if (/^<\/code>/i.test(part)) inCode = Math.max(0, inCode - 1);
        return part;
      }
      if (inCode || !part) return part;
      let s = part;
      s = s.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
      s = s.replace(
        /(^|[\s(\u00a0]|&nbsp;)\*([^*\s][^*]*)\*(?=[\s).,;:!?\u00a0]|&nbsp;|$)/g,
        '$1<i>$2</i>'
      );
      // Markdown links: [label](https://…)
      s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi, (_, label, href) =>
        isSafeHref(href) ? `<a href="${href}">${label}</a>` : _
      );
      // Bare URLs — skip text already inside an <a> we just inserted
      s = s
        .split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi)
        .map((chunk) =>
          /^<a\b/i.test(chunk) ? chunk : linkifyBareUrls(chunk, { linkifyAtEnd })
        )
        .join('');
      return s;
    })
    .join('');
  return t;
};

/** Inline markdown → safe inline HTML: `code`, **bold**, *italic*, links. */
export const inlineMd = (raw) =>
  convertMarkdownMarkersInText(escapeHtml(raw), { linkifyAtEnd: true });

/**
 * Convert leftover markdown markers in an HTML string's text segments.
 * Used for typing + inline paste so "**bold**" becomes <b>bold</b> even when
 * the clipboard/DOM still has the asterisks as literal text.
 */
export const applyInlineMarkdown = (html) => {
  const stripped = stripEmptyInlineCode(html);
  if (!stripped || !/[*`\[\]]|https?:\/\//i.test(stripped)) return stripped;
  // Fix links that were truncated mid-URL on earlier keystrokes before we
  // skip re-linkifying inside existing <a> tags.
  const healed = healTruncatedUrlLinks(stripped);
  // Split on tags so we never depend on (^|>) zero-width matches; skip
  // text that already sits inside <code>…</code> or <a>…</a>. Re-linkifying
  // URL text inside an existing <a> nests another anchor every keystroke,
  // which makes react-contenteditable rewrite innerHTML and snap the caret
  // to the end of the line.
  let inCode = 0;
  let inAnchor = 0;
  const converted = String(healed)
    .split(/(<[^>]+>)/g)
    .map((part) => {
      if (part && part.startsWith('<')) {
        if (/^<code\b/i.test(part)) inCode += 1;
        else if (/^<\/code>/i.test(part)) inCode = Math.max(0, inCode - 1);
        else if (/^<a\b/i.test(part)) inAnchor += 1;
        else if (/^<\/a>/i.test(part)) inAnchor = Math.max(0, inAnchor - 1);
        return part;
      }
      if (inCode || inAnchor) return part;
      return convertMarkdownMarkersInText(part, { linkifyAtEnd: false });
    })
    .join('');
  return stripEmptyInlineCode(converted);
};

/** Normalize a table cell (or any rich snippet) so leftover **bold** becomes <b>. */
export const normalizeRichHtml = (value) => applyInlineMarkdown(String(value ?? ''));

/** Walk a block tree and convert leftover **bold** / *italic* / `code` markers. */
export const applyInlineMarkdownToTree = (blocks) =>
  (blocks || []).map((b) => {
    // Raw code must keep backticks / asterisks literal
    if (b.type === 'code') {
      return {
        ...b,
        children: b.children?.length ? applyInlineMarkdownToTree(b.children) : b.children,
      };
    }
    const next = {
      ...b,
      content: b.content ? applyInlineMarkdown(b.content) : b.content,
      children: b.children?.length ? applyInlineMarkdownToTree(b.children) : b.children,
    };
    if (b.type === 'table' && b.props?.tableData) {
      const { headers = [], rows = [] } = b.props.tableData;
      next.props = {
        ...b.props,
        tableData: {
          headers: headers.map((h) =>
            Array.isArray(h) ? h : normalizeRichHtml(h)
          ),
          rows: rows.map((row) =>
            (row || []).map((c) => (Array.isArray(c) ? c : normalizeRichHtml(c)))
          ),
        },
      };
    }
    return next;
  });
